import React, { useState } from "react";
import { useAccounts, useCreateAccount, useDeleteAccount, useTestAccount, useReference, useUpdateAccount } from "@/lib/queries";
import { Plus, Trash2, Lock, CheckCircle2, XCircle, ShieldCheck, Send, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { formatRelative, RETAILER_COLORS } from "@/lib/cr";

const SELECT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308]";
const INPUT = "w-full bg-[#050505] border border-[#262626] text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]";
const LABEL = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5";

const EMPTY = { retailer_id: "", label: "", username: "", password: "", notes: "" };

export default function AccountsPage() {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const { data: ref } = useReference();
  const { data: accounts = [] } = useAccounts();
  const createAcc = useCreateAccount();
  const updateAcc = useUpdateAccount();
  const deleteAcc = useDeleteAccount();
  const testAcc = useTestAccount();

  const startEdit = (a) => {
    setEditingId(a.id);
    // Username and password are encrypted at rest — leave blank in edit mode.
    // Backend's PATCH treats blanks as "keep existing" so we never silently
    // erase credentials by re-submitting.
    setForm({
      retailer_id: a.retailer_id,
      label: a.label || "",
      username: "",
      password: "",
      notes: a.notes || "",
    });
    setTimeout(() => document.querySelector('[data-testid="account-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = (e) => {
    e.preventDefault();
    if (editingId) {
      // PATCH: blanks = keep existing
      if (!form.label) {
        toast.error("Label is required");
        return;
      }
      updateAcc.mutate(
        { id: editingId, body: { label: form.label, username: form.username, password: form.password, notes: form.notes } },
        {
          onSuccess: () => { toast.success("Account updated"); cancelEdit(); },
          onError: (err) => toast.error("Failed to update", { description: err?.response?.data?.detail }),
        }
      );
      return;
    }
    if (!form.retailer_id || !form.label || !form.username) {
      toast.error("Retailer, label and username are required");
      return;
    }
    createAcc.mutate(form, {
      onSuccess: () => {
        toast.success("Account saved (encrypted)");
        setForm(EMPTY);
      },
      onError: () => toast.error("Failed to save account"),
    });
  };

  // group by retailer for display
  const byRetailer = accounts.reduce((acc, a) => {
    (acc[a.retailer_id] = acc[a.retailer_id] || []).push(a);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl w-full mx-auto" data-testid="accounts-page">
      <header className="mb-4">
        <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
          Store <span className="text-[#EAB308]">accounts</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Save multiple logins per retailer for scalp mode. Credentials are encrypted at rest with Fernet (AES-128).
        </p>
        <div className="mt-3 flex items-start gap-2 text-[11px] text-[#A3A3A3] bg-[#0d0d0d] border border-[#262626] p-3 rounded-sm">
          <ShieldCheck className="w-4 h-4 text-[#EAB308] shrink-0 mt-0.5" />
          <div>
            <strong className="text-white">MOCKED LOGIN:</strong> the &ldquo;Test login&rdquo; button does <em>not</em> contact any real retailer — it simulates a login attempt. Real automated logins violate retailer ToS and will get accounts banned.
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <form
          onSubmit={submit}
          className="bg-[#0d0d0d] border border-[#262626] p-5 rounded-sm space-y-4"
          data-testid="account-form"
        >
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              {editingId ? <><Pencil className="w-4 h-4 text-[#3B82F6]" /> Edit account</> : <><Plus className="w-4 h-4 text-[#EAB308]" /> New account</>}
            </span>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="text-[#737373] hover:text-white" title="Cancel edit" data-testid="account-cancel-edit">
                <X className="w-4 h-4" />
              </button>
            )}
          </h2>

          {editingId && (
            <div className="bg-[#3B82F6]/5 border border-[#3B82F6]/30 rounded-sm p-2.5 text-[11px] text-[#93C5FD] leading-relaxed" data-testid="account-edit-hint">
              <strong className="text-white">Editing encrypted account.</strong> Username and password are blank by design — they&apos;re encrypted at rest and can&apos;t be re-shown. Leave them blank to keep current; only fill in what you want to change.
            </div>
          )}

          <div>
            <div className={LABEL}>Retailer {editingId ? "" : "*"}</div>
            <select
              data-testid="account-retailer-select"
              className={SELECT}
              value={form.retailer_id}
              onChange={(e) => setForm({ ...form, retailer_id: e.target.value })}
              disabled={!!editingId}
              title={editingId ? "Retailer cannot be changed on an existing account — delete and recreate if needed." : ""}
            >
              <option value="">Choose retailer...</option>
              {(ref?.retailers || []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.region})
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className={LABEL}>Label *</div>
            <input
              data-testid="account-label-input"
              className={INPUT}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Main, Burner #1"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className={LABEL}>Username / email {editingId ? "" : "*"}</div>
              <input
                data-testid="account-username-input"
                className={INPUT}
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder={editingId ? "(unchanged — leave blank to keep current)" : "john@example.com"}
              />
            </div>
            <div>
              <div className={LABEL}>Password <span className="text-[#737373] font-normal normal-case tracking-normal">{editingId ? "(blank = keep current)" : "(leave blank for passwordless stores — BIG W, magic-link, OAuth)"}</span></div>
              <input
                data-testid="account-password-input"
                type="password"
                className={INPUT}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingId ? "(unchanged — leave blank to keep current)" : "leave blank if store uses email magic link"}
              />
            </div>
          </div>

          <div>
            <div className={LABEL}>Notes</div>
            <input
              data-testid="account-notes-input"
              className={INPUT}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. uses 2FA, backup card linked"
            />
          </div>

          <button
            type="submit"
            data-testid="account-submit-btn"
            disabled={createAcc.isPending || updateAcc.isPending}
            className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs py-2.5 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Lock className="w-4 h-4" /> {editingId ? "Save changes" : "Encrypt & save"}
          </button>
        </form>

        <div className="space-y-3">
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider">
            Saved accounts ({accounts.length})
          </h2>
          {accounts.length === 0 ? (
            <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#737373] rounded-sm text-sm">
              No accounts yet.
            </div>
          ) : (
            Object.entries(byRetailer).map(([rid, list]) => {
              const retailer = ref?.retailers?.find((r) => r.id === rid);
              const color = RETAILER_COLORS[rid] || "#A3A3A3";
              return (
                <div key={rid} className="bg-[#0d0d0d] border border-[#262626] rounded-sm">
                  <div className="px-4 py-2 border-b border-[#262626] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm"
                        style={{ color, borderColor: `${color}55`, backgroundColor: "#0a0a0a" }}
                      >
                        {retailer?.name || rid}
                      </span>
                      <span className="text-[10px] font-mono text-[#737373]">{list.length} account(s)</span>
                    </div>
                  </div>
                  {list.map((a) => (
                    <div
                      key={a.id}
                      data-testid={`account-item-${a.id}`}
                      className="px-4 py-3 border-b border-[#1A1A1A] last:border-b-0 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white font-semibold truncate">{a.label}</div>
                        <div className="text-[11px] font-mono text-[#A3A3A3]">{a.username_masked}</div>
                        {a.notes && <div className="text-[10px] text-[#737373] mt-0.5">{a.notes}</div>}
                        {a.last_login_at && (
                          <div className="text-[10px] font-mono mt-1 flex items-center gap-1">
                            {a.last_login_status === "ok" ? (
                              <CheckCircle2 className="w-3 h-3 text-[#22C55E]" />
                            ) : (
                              <XCircle className="w-3 h-3 text-[#EF4444]" />
                            )}
                            <span className={a.last_login_status === "ok" ? "text-[#22C55E]" : "text-[#EF4444]"}>
                              {a.last_login_status === "ok" ? "Login OK" : "Login failed"}
                            </span>
                            <span className="text-[#737373]">· {formatRelative(a.last_login_at)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button
                          data-testid={`account-edit-${a.id}`}
                          onClick={() => startEdit(a)}
                          className="p-2 text-[#737373] hover:text-[#3B82F6] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                          title="Edit account"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`account-test-${a.id}`}
                          onClick={() =>
                            testAcc.mutate(a.id, {
                              onSuccess: (r) => toast.success(r.ok ? "Login OK (sim)" : "Login failed (sim)"),
                            })
                          }
                          className="p-2 text-[#737373] hover:text-[#EAB308] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                          title="Simulated login test"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`account-delete-${a.id}`}
                          onClick={() =>
                            deleteAcc.mutate(a.id, { onSuccess: () => toast.success("Account removed") })
                          }
                          className="p-2 text-[#737373] hover:text-[#EF4444] hover:bg-[#1A1A1A] rounded-sm transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
